import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ChevronLeft, ChevronRight, Filter, X } from "lucide-react";
import Breadcrumb from "../Pages/Breadcrumb";
import { useQuery } from "@tanstack/react-query";
import { useLanguage } from "../../store/language-context";
import { useTranslation } from "react-i18next";

const PlanPage = () => {
  const { id } = useParams();
  const { URL } = useLanguage();
  const { t } = useTranslation();

  const { data, error, isLoading } = useQuery({
    queryKey: ['category-data', id, URL],
    queryFn: async () => {
      const response = await axios.get(`${URL}/categories/${id}`);
      if (response.status !== 200) throw new Error(t('custom.err'));
      return response.data?.data
    }
  });
  const [searchName, setSearchName] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const subjectsPerPage = 6;
  const [showSidebar, setShowSidebar] = useState(false);

  const filteredSubjects = data?.subjects?.filter((subject) => {
    const matchesName = searchName
      ? subject.title.toLowerCase().includes(searchName.toLowerCase())
      : data?.subjects;
    return matchesName;
  });

  const totalPages = Math.ceil(data?.subjects.length / subjectsPerPage);

  const paginate = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  return (
    <>
      <Breadcrumb pages={[t('routes.training-plan'), `${data?.title || t('routes.training-plan-details')}`]} />

      <div className="container mx-auto ">
        {/* Toggle Button for Sidebar */}
        {!showSidebar && (
          <button
            className="py-2 bg-[#6A2F38] text-white rounded-md flex items-center !my-6 px-4"
            onClick={() => setShowSidebar(true)}
          >
            <Filter className="mx-2" color="white" />
            {t('training-courses.filter')}
          </button>
        )}

        <div className="two-category-section row items-start">
          <div className="sec-filter col-lg-3">
            {showSidebar && (
              <aside className="w-full bg-white shadow-md rounded-lg max-lg:mb-5 max-lg:px-4">
                {/* Close Button inside the sidebar */}
                <div className="p-2 text-white bg-primary-tw flex justify-between items-center rounded">
                  <span className="flex items-center">
                    <Filter size={20} color="white" className="mx-2" /> {t('training-courses.filter')}
                  </span>
                  <button
                    className=" text-white p-1 closeIcon"
                    onClick={() => setShowSidebar(false)}
                  >
                    <X size={12} color="white" />
                  </button>
                </div>
                <div className="search-method p-2">
                  {/* Search Input */}
                  <label className="block text-base text-end text-semi-gray">{t('custom.course-name')}</label>
                  <input
                    type="text"
                    value={searchName}
                    onChange={(e) => setSearchName(e.target.value)}
                    placeholder={t('training-courses.search-course-name')}
                    className="select-ui"
                  />
                </div>
              </aside>
            )}
          </div>
          {
            isLoading ?
              <div className={`category-part w-full ${showSidebar ? "col-9" : "col-12"}`}>
                <div className="w-full h-56 l-load rounded-lg"></div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 py-4">
                  {
                    [...Array(3)].map((_, i) => (
                      <div key={i} className="w-full h-44 rounded-lg l-load relative grid place-items-center">
                        <div className="absolute top-8 w-48 h-5 rounded-lg l-load-dt"></div>
                        <div className="w-36 h-10 l-load-dt rounded-lg absolute bottom-5"></div>
                      </div>
                    ))
                  }
                </div>
              </div>
              :
              error
                ?
                <p className="text-center text-red-500">{t('custom.err')}</p>
                :
                <div className={`category-part w-full ${showSidebar ? "col-9" : "col-12"}`}>

                  {/* Category Header */}
                  <div
                    className="relative w-full h-56 bg-cover bg-center rounded-lg overflow-hidden"
                    style={{
                      backgroundImage: `url(${data?.image || 'https://ivorytraining.com/TrainingPlan/HpWyAyoovWza2p95CdnfYX9clQKB1NODK86Gisjl.jpg'})`
                    }}

                  >
                    <div className="absolute inset-0 bg-opacity-70 bg-gradient-to-r from-[#6A2F38] to-[#6A2F38]/74 flex items-center justify-center">
                      <h1 className="text-white text-2xl font-semibold">{data?.title}</h1>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-col lg:flex-row gap-4">
                    {/* Courses Grid */}
                    <div className={`w-full }`}>
                      {filteredSubjects.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {filteredSubjects.map((subject) => (
                            <div key={subject.id} className="border rounded-lg p-4 text-center bg-white plan-details">
                              <span className="text-gray-600 fs-4 mt-2 mb-2">{subject.title}</span>
                              <Link to={`/subject-courses/${subject.id}`} className="mt-4 d-inline-block bg-secondary-tw text-white py-2 px-4 rounded-md">
                                {t('custom.show')} ←
                              </Link>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-center text-gray-500"> {t('custom.no-data')}</p>
                      )}


                      {/* Pagination */}
                      {totalPages > 1 && (
                        <div className="flex justify-center mt-6 mb-6 flex-wrap">
                          <button
                            onClick={() => paginate(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="border rounded text-[#D9D9D9] m-3 p-2"
                          >
                            <ChevronRight />
                          </button>

                          {[...Array(totalPages).keys()].map((num) => (
                            <button
                              key={num}
                              onClick={() => paginate(num + 1)}
                              className={`mx-1 p-2 ${num + 1 === currentPage ? "text-[rgb(212,173,137)]" : "text-gray-700 bg-white"
                                }`}
                            >
                              {num + 1}
                            </button>
                          ))}

                          <button
                            onClick={() => paginate(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="border rounded text-[#D9D9D9] m-3 p-2"
                          >
                            <ChevronLeft />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
          }
        </div>
      </div>
    </>
  );
};

export default PlanPage;